import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import * as api from '../services/api';
import type { ApiToken } from '../services/api';
import { Key, Trash2, Copy, Plus, Clock, Shield, ArrowLeft, Terminal, BookOpen, CheckCircle } from 'lucide-react';
import './SettingsPage.css';

export default function SettingsPage() {
    const { user } = useAuth();
    const [tokens, setTokens] = useState<ApiToken[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [tokenName, setTokenName] = useState('');
    const [newToken, setNewToken] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    
    const [savingKyc, setSavingKyc] = useState(false);
    const [kycForm, setKycForm] = useState({
        full_name: user?.full_name || '',
        company_name: user?.company_name || '',
        vat_number: user?.vat_number || '',
        billing_address: user?.billing_address || '',
        phone: user?.phone || '',
        proof_of_address_url: user?.proof_of_address_url || '',
        copy_of_id_url: user?.copy_of_id_url || ''
    });

    useEffect(() => {
        if (user) {
            setKycForm({
                full_name: user.full_name || '',
                company_name: user.company_name || '',
                vat_number: user.vat_number || '',
                billing_address: user.billing_address || '',
                phone: user.phone || '',
                proof_of_address_url: user.proof_of_address_url || '',
                copy_of_id_url: user.copy_of_id_url || ''
            });
        }
    }, [user]);

    const handleKycUpload = async (field: 'proof_of_address_url' | 'copy_of_id_url', e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        
        // Use a generic placeholder while uploading
        setKycForm(prev => ({ ...prev, [field]: 'Uploading...' }));
        
        const res = await api.uploadKycDocument(file.name, file);
        if (res.success && res.data) {
            const uploadedUrl = res.data.url;
            setKycForm(prev => ({ ...prev, [field]: uploadedUrl }));
        } else {
            alert(res.error || 'Failed to upload document.');
            const fallbackUrl = user?.[field] || '';
            setKycForm(prev => ({ ...prev, [field]: fallbackUrl }));
        }
        
        // Reset file input so user can re-upload same file if needed
        e.target.value = '';
    };

    const handleSaveKyc = async () => {
        setSavingKyc(true);
        const res = await api.updateProfile(kycForm);
        if (res.success) {
            alert('Settings saved successfully.');
            // Update auth context
        } else {
            alert(res.error || 'Failed to save settings.');
        }
        setSavingKyc(false);
    };

    useEffect(() => {
        loadTokens();
    }, []);

    const loadTokens = async () => {
        const res = await api.getApiTokens();
        if (res.success && res.data) {
            setTokens(res.data);
        }
        setLoading(false);
    };

    const handleCreateToken = async () => {
        if (!tokenName.trim()) return;
        setCreating(true);
        const res = await api.createApiToken(tokenName.trim());
        if (res.success && res.data) {
            setNewToken(res.data.token || null);
            setTokenName('');
            loadTokens();
        }
        setCreating(false);
    };

    const handleRevokeToken = async (id: string) => {
        if (!confirm('Are you sure you want to revoke this token? Any CLI or CI/CD using it will stop working.')) return;
        await api.revokeApiToken(id);
        loadTokens();
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="settings-page">
            {/* Sidebar */}
            <aside className="settings-sidebar">
                <div className="sidebar-brand">
                    <h1>⚡ Wranglr</h1>
                </div>
                <ul className="sidebar-links">
                    <li>
                        <Link to="/dashboard">
                            <ArrowLeft size={18} /> Back to Dashboard
                        </Link>
                    </li>
                    <li>
                        <a href="#identity" onClick={(e) => scrollToSection(e, 'identity')} className="active">
                            <BookOpen size={18} /> Billing & Identity
                        </a>
                    </li>
                    <li>
                        <a href="#tokens" onClick={(e) => scrollToSection(e, 'tokens')}>
                            <Key size={18} /> API Tokens
                        </a>
                    </li>
                    <li>
                        <a href="#guide" onClick={(e) => scrollToSection(e, 'guide')}>
                            <Terminal size={18} /> Quick Start
                        </a>
                    </li>
                </ul>
            </aside>

            {/* Main Content */}
            <main className="settings-main">
                <div className="settings-container">
                    {/* Page Header */}
                    <div className="settings-page-header">
                        <h1>
                            <Key size={28} />
                            API Tokens
                        </h1>
                        <p>Generate and manage tokens for the Wranglr CLI and CI/CD pipelines.</p>
                    </div>

                    {/* New Token Alert */}
                    {newToken && (
                        <div className="token-alert">
                            <h3>
                                <CheckCircle size={18} />
                                Token created! Copy it now — you won't see it again.
                            </h3>
                            <div className="token-display">
                                <code>{newToken}</code>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => copyToClipboard(newToken)}
                                    style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                >
                                    <Copy size={14} />
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                            <div className="token-hint">
                                Use with CLI: <code>wranglr login --token YOUR_TOKEN</code><br />
                                Or set as env variable: <code>WRANGLR_TOKEN=YOUR_TOKEN</code>
                            </div>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setNewToken(null)}
                                style={{ marginTop: '0.75rem' }}
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    {/* KYC Identity Card */}
                    <div className="settings-section" id="identity">
                        <div className="settings-section-header">
                            <BookOpen size={18} />
                            <h3>Billing & Identity (KYC)</h3>
                        </div>
                        <div className="token-form" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Full Name</label>
                                    <input type="text" placeholder="Full Name" value={kycForm.full_name} onChange={e => setKycForm({...kycForm, full_name: e.target.value})} style={{ width: '100%' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Company Name (Optional)</label>
                                    <input type="text" placeholder="Company Name" value={kycForm.company_name} onChange={e => setKycForm({...kycForm, company_name: e.target.value})} style={{ width: '100%' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>VAT Number (Optional)</label>
                                    <input type="text" placeholder="VAT Number" value={kycForm.vat_number} onChange={e => setKycForm({...kycForm, vat_number: e.target.value})} style={{ width: '100%' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Phone Number</label>
                                    <input type="text" placeholder="Phone Number" value={kycForm.phone} onChange={e => setKycForm({...kycForm, phone: e.target.value})} style={{ width: '100%' }} />
                                </div>
                            </div>
                            
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Billing Address</label>
                            <input type="text" placeholder="Billing Address" style={{ marginBottom: '1rem', width: '100%' }} value={kycForm.billing_address} onChange={e => setKycForm({...kycForm, billing_address: e.target.value})} />
                            
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Proof of Address (Optional)</label>
                                    <input type="file" accept="image/*,.pdf" onChange={e => handleKycUpload('proof_of_address_url', e)} style={{ width: '100%', marginBottom: '0.5rem' }} />
                                    {kycForm.proof_of_address_url && kycForm.proof_of_address_url !== 'Uploading...' && (
                                        <a href={`https://api.wranglr.co.za${kycForm.proof_of_address_url}?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer" style={{ fontSize: '0.875rem', color: 'var(--primary)' }}>
                                            View current document
                                        </a>
                                    )}
                                    {kycForm.proof_of_address_url === 'Uploading...' && <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Uploading...</span>}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Copy of ID (Optional)</label>
                                    <input type="file" accept="image/*,.pdf" onChange={e => handleKycUpload('copy_of_id_url', e)} style={{ width: '100%', marginBottom: '0.5rem' }} />
                                    {kycForm.copy_of_id_url && kycForm.copy_of_id_url !== 'Uploading...' && (
                                        <a href={`https://api.wranglr.co.za${kycForm.copy_of_id_url}?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer" style={{ fontSize: '0.875rem', color: 'var(--primary)' }}>
                                            View current document
                                        </a>
                                    )}
                                    {kycForm.copy_of_id_url === 'Uploading...' && <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Uploading...</span>}
                                </div>
                            </div>
                            
                            <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} onClick={handleSaveKyc} disabled={savingKyc}>
                                {savingKyc ? 'Saving...' : 'Save Profile'}
                            </button>
                        </div>
                    </div>

                    {/* Generate Token Card */}
                    <div className="settings-section" id="tokens">
                        <div className="settings-section-header">
                            <Plus size={18} />
                            <h3>Generate New Token</h3>
                        </div>
                        <div className="token-form">
                            <input
                                type="text"
                                value={tokenName}
                                onChange={e => setTokenName(e.target.value)}
                                placeholder="Token name (e.g. 'My Laptop CLI', 'GitHub Actions')"
                                onKeyDown={e => e.key === 'Enter' && handleCreateToken()}
                            />
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateToken}
                                disabled={creating || !tokenName.trim()}
                            >
                                {creating ? 'Creating...' : 'Generate Token'}
                            </button>
                        </div>
                    </div>

                    {/* Active Tokens Card */}
                    <div className="settings-section">
                        <div className="settings-section-header">
                            <Shield size={18} />
                            <h3>Active Tokens</h3>
                        </div>

                        {loading ? (
                            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1.5rem' }}>
                                Loading tokens...
                            </p>
                        ) : tokens.length === 0 ? (
                            <div className="token-empty">
                                <Key size={40} className="token-empty-icon" />
                                <p>No API tokens yet. Generate one above to get started with the CLI.</p>
                            </div>
                        ) : (
                            <div className="token-list">
                                {tokens.map(token => (
                                    <div key={token.id} className="token-item">
                                        <div className="token-item-info">
                                            <div className="token-item-name">
                                                <Key size={14} />
                                                {token.name}
                                            </div>
                                            <div className="token-item-meta">
                                                <span>
                                                    Created: {new Date(token.created_at).toLocaleDateString()}
                                                </span>
                                                {token.last_used_at && (
                                                    <span>
                                                        <Clock size={12} />
                                                        Last used: {new Date(token.last_used_at).toLocaleDateString()}
                                                    </span>
                                                )}
                                                {token.expires_at && (
                                                    <span>
                                                        Expires: {new Date(token.expires_at).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleRevokeToken(token.id)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                        >
                                            <Trash2 size={14} /> Revoke
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quick Start Guide */}
                    <div className="settings-divider" id="guide">
                        📚 Quick Start Guide
                    </div>

                    <div className="settings-section">
                        <div className="settings-section-header">
                            <Terminal size={18} />
                            <h3>Deploy from your terminal in 3 steps</h3>
                        </div>

                        <div className="quickstart-steps">
                            <div className="quickstart-step">
                                <span className="step-number">1</span>
                                <div className="step-content">
                                    <strong>Install the CLI</strong>
                                    <p>Install the Wranglr CLI globally using npm.</p>
                                    <div className="step-code">
                                        <code>npm install -g @wranglr/cli</code>
                                        <button onClick={() => copyToClipboard('npm install -g @wranglr/cli')} title="Copy">
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="quickstart-step">
                                <span className="step-number">2</span>
                                <div className="step-content">
                                    <strong>Authenticate</strong>
                                    <p>Login using the API token you generated above.</p>
                                    <div className="step-code">
                                        <code>wranglr login --token &lt;YOUR_TOKEN&gt;</code>
                                    </div>
                                </div>
                            </div>

                            <div className="quickstart-step">
                                <span className="step-number">3</span>
                                <div className="step-content">
                                    <strong>Deploy</strong>
                                    <p>Navigate to your project and deploy. Files are uploaded automatically.</p>
                                    <div className="step-code">
                                        <code>cd my-website{'\n'}wranglr deploy ./dist --site my-site.com</code>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="help-footer">
                            <p>Need more help? Check out the full documentation.</p>
                            <Link to="/docs" className="btn btn-secondary">
                                <BookOpen size={16} />
                                View Full Docs →
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
